"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowRight, ArrowUpRight } from "@/components/icons";
import { FeatureCard, HeroGlow, PricingCtaSection, SectionHeader } from "@/components/ui/";
import { useUptime } from "@/hooks/use-uptime";
import { STATS, CONTACT_SALES_URL, STATUS_URL } from "@/lib/constants";
import BadgesSection from "@/components/sections/shared/badges";
import CompareAlternatives from "@/components/sections/products/shared/compare-alternatives";
import { enterpriseFeatures, infrastructureItems, deploymentItems, supportItems } from "./data";

const MotionDiv = motion.div;

/* Grids orchestrate their tiles' reveals: the container triggers on scroll and
   staggers the FeatureCards' inner content. Each card carries its own surface
   and border, so only the content inside animates. */
const gridVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

export default function EnterpriseSection() {
  const prefersReducedMotion = useReducedMotion();
  const uptime = useUptime();
  const uptimeDisplay = `${uptime.toFixed(3)}%`;

  const fadeUp = (delay = 0) => ({
    initial: prefersReducedMotion ? {} : { opacity: 0, y: 24 },
    whileInView: prefersReducedMotion ? {} : { opacity: 1, y: 0 },
    viewport: scrollRevealViewport,
    transition: { duration: 0.55, delay },
  });

  const heroStats = [
    { value: STATS.customers, label: "Customers" },
    { value: STATS.countries, label: "Countries" },
    { value: uptimeDisplay, label: "Uptime" },
    { value: "Millions of", label: "Real IPs" },
  ];

  const slaStats = [
    {
      value: uptimeDisplay,
      label: "Uptime Guarantee",
      description: "Independently monitored by BetterStack.",
    },
    {
      value: "<0.6s",
      label: "Avg Response",
      description: "Ultra-low latency across all proxy types.",
    },
    {
      value: "99.5%+",
      label: "Success Rate",
      description: "Across all major websites.",
    },
  ];

  return (
    <main id="main-content" tabIndex={-1}>
      {/* ── 1. Hero ──────────────────────────────────────────────────────── */}
      <section
        className="border-border relative overflow-y-clip border-b pt-32 pb-24"
        aria-labelledby="enterprise-heading"
      >
        <HeroGlow align="left" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)}>
            <p className="text-muted-foreground mb-3 text-sm font-medium">Enterprise</p>
          </MotionDiv>

          <MotionDiv {...fadeUp(0.08)}>
            <h1
              id="enterprise-heading"
              className="text-foreground mb-8 max-w-4xl text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
            >
              Enterprise-grade proxy infrastructure.
            </h1>
          </MotionDiv>

          <MotionDiv {...fadeUp(0.16)} className="max-w-3xl">
            <p className="text-muted-foreground light:text-foreground/80 text-base leading-relaxed">
              Proxidize is a managed cloud proxy provider trusted by {STATS.customers} of teams.
              Powerful enough for enterprise-scale operations, with dedicated support, redundant
              infrastructure, and the security standards your team expects before signing.
            </p>
          </MotionDiv>

          <MotionDiv {...fadeUp(0.24)} className="mt-10 flex flex-wrap gap-3">
            <span className="cta-glow-btn-wrap">
              <Link href={CONTACT_SALES_URL} className="cta-glow-btn-inner">
                Contact Sales
                <ArrowRight size={16} className="ml-2" aria-hidden="true" />
              </Link>
            </span>
          </MotionDiv>

          {/* Stat strip */}
          <MotionDiv
            {...fadeUp(0.32)}
            className="mt-16 grid grid-cols-2 gap-8 lg:grid-cols-4 lg:gap-12"
          >
            {heroStats.map((stat) => (
              <div key={stat.label} className="flex flex-col gap-1">
                <span className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
                  {stat.value}
                </span>
                <span className="text-muted-foreground text-sm">{stat.label}</span>
              </div>
            ))}
          </MotionDiv>
        </div>
      </section>

      {/* ── 2. Enterprise features ───────────────────────────────────────── */}
      <section
        className="full-bleed band-wash bg-band py-section border-border border-b"
        aria-labelledby="features-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)} className="mb-14 max-w-2xl">
            <SectionHeader
              eyebrow="Enterprise Features"
              id="features-heading"
              heading="Built for teams that depend on reliable data."
              body="Clean IPs, simple access, transparent usage, and real support, with the security and account controls enterprise teams require."
            />
          </MotionDiv>

          <MotionDiv
            variants={gridVariants}
            initial={prefersReducedMotion ? "visible" : "hidden"}
            whileInView="visible"
            viewport={scrollRevealViewport}
            className="grid gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3"
          >
            {enterpriseFeatures.map((feature) => (
              <div key={feature.title} className="feature-tile h-full *:h-full">
                <FeatureCard
                  Visual={feature.Visual}
                  title={feature.title}
                  description={feature.description}
                  wash
                  className="card-hover card-spotlight"
                />
              </div>
            ))}
          </MotionDiv>
        </div>
      </section>

      {/* ── 3. Dedicated infrastructure ──────────────────────────────────── */}
      <section
        className="py-section border-border border-b"
        aria-labelledby="infrastructure-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)} className="mb-14 max-w-2xl">
            <SectionHeader
              eyebrow="Dedicated Infrastructure"
              id="infrastructure-heading"
              heading="We run our own network."
              body="We write our own software, we run our own network, and we maintain direct control over every layer of the stack."
            />
          </MotionDiv>

          <MotionDiv
            variants={gridVariants}
            initial={prefersReducedMotion ? "visible" : "hidden"}
            whileInView="visible"
            viewport={scrollRevealViewport}
            className="grid auto-rows-fr gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4"
          >
            {infrastructureItems.map((item) => (
              <div key={item.title} className="feature-tile h-full *:h-full">
                <FeatureCard
                  Visual={item.Visual}
                  title={item.title}
                  description={item.description}
                  wash
                  className="card-hover card-spotlight"
                />
              </div>
            ))}
          </MotionDiv>
        </div>
      </section>

      {/* ── 4. SLAs & reliability ────────────────────────────────────────── */}
      <section
        className="full-bleed band-wash bg-band py-section border-border border-b"
        aria-labelledby="sla-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)} className="mb-14 max-w-2xl">
            <SectionHeader
              eyebrow="SLAs & Reliability"
              id="sla-heading"
              heading="Uptime you can build on."
              body="Proven uptime backed by redundant infrastructure across multiple data centers globally."
            />
          </MotionDiv>

          <div className="grid auto-rows-fr gap-4 sm:grid-cols-3 sm:gap-5">
            {slaStats.map((stat, i) => (
              <div
                key={stat.label}
                className="bg-card card-wash card-hover border-border relative h-full overflow-hidden rounded-2xl border p-6"
              >
                <MotionDiv {...fadeUp(i * 0.08)}>
                  <p
                    className="text-3xl font-bold tracking-tight sm:text-4xl"
                    style={{ color: "var(--brand-green)" }}
                  >
                    {stat.value}
                  </p>
                  <p className="text-foreground mt-1 text-sm font-semibold">{stat.label}</p>
                  <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
                    {stat.description}
                  </p>
                </MotionDiv>
              </div>
            ))}
          </div>

          <MotionDiv {...fadeUp(0.24)} className="mt-8">
            <Link
              href={STATUS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
            >
              View live status
              <ArrowUpRight size={14} aria-hidden="true" />
            </Link>
          </MotionDiv>
        </div>
      </section>

      {/* ── 5. Custom deployments ────────────────────────────────────────── */}
      <section className="py-section border-border border-b" aria-labelledby="deployments-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)} className="mb-14 max-w-2xl">
            <SectionHeader
              eyebrow="Custom Deployments"
              id="deployments-heading"
              heading="Deploy a proxy pool in minutes, not days."
              body="Our API-first architecture means no waiting for manual setup."
            />
          </MotionDiv>

          <MotionDiv
            variants={gridVariants}
            initial={prefersReducedMotion ? "visible" : "hidden"}
            whileInView="visible"
            viewport={scrollRevealViewport}
            className="grid auto-rows-fr gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3"
          >
            {deploymentItems.map((item) => (
              <div key={item.title} className="feature-tile h-full *:h-full">
                <FeatureCard
                  Visual={item.Visual}
                  title={item.title}
                  description={item.description}
                  wash
                  className="card-hover card-spotlight"
                />
              </div>
            ))}
          </MotionDiv>
        </div>
      </section>

      {/* ── 6. Priority support ──────────────────────────────────────────── */}
      <section
        className="full-bleed band-wash bg-band py-section border-border border-b"
        aria-labelledby="support-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)} className="mb-14 max-w-2xl">
            <SectionHeader
              eyebrow="Priority Support"
              id="support-heading"
              heading="Real engineers, around the clock."
              body="Premium support via live chat and email. Enterprise plans come with a dedicated account manager."
            />
          </MotionDiv>

          <MotionDiv
            variants={gridVariants}
            initial={prefersReducedMotion ? "visible" : "hidden"}
            whileInView="visible"
            viewport={scrollRevealViewport}
            className="grid auto-rows-fr gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3"
          >
            {supportItems.map((item) => (
              <div key={item.title} className="feature-tile h-full *:h-full">
                <FeatureCard
                  Visual={item.Visual}
                  title={item.title}
                  description={item.description}
                  wash
                  className="card-hover card-spotlight"
                />
              </div>
            ))}
          </MotionDiv>
        </div>
      </section>

      {/* ── 7. Recognition (certifications) ──────────────────────────────── */}
      <BadgesSection />

      {/* ── 8. Compare alternatives ──────────────────────────────────────── */}
      <CompareAlternatives />

      {/* ── 9. Closing CTA ───────────────────────────────────────────────
           The site-wide closing band, same as every other page: the warm
           `.cta-wash` ground, the same headline scale, the same two buttons and
           perk row. Only the headline is enterprise's own — that is exactly the
           seam PricingCtaSection exposes. This section used to be hand-rolled
           here (no wash, one button, its own perks), which is why it read as a
           different CTA from the homepage's. */}
      <PricingCtaSection
        id="enterprise-cta-heading"
        heading="Let's build a plan around your scale."
        body="Start free today, or talk to our team to build a plan around your scale."
      />
    </main>
  );
}
